import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Grid from '@mui/material/Grid';
import { Alert, AlertTitle, useMediaQuery } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DatedSessionBookingStatus } from '../api';
import { ScheduleSession, usePassScheduleData } from './hooks/usePassScheduleData';
import { useManagePasses } from './ManagePassesContext';
import { getExperienceName, getNewestPass } from './utils/passUtils';
import { getPassCancelHoursBefore, isPassCancelAllowed } from './utils/passCancellationUtils';
import { getDateFormat, getTimeFormat } from '../utils/getDateAndTimeFormat';
import { useLanguage } from '../utils/GeneralUtils';
import { ScheduleHeader } from './components/ScheduleHeader';
import { HorizontalDateStrip } from './components/HorizontalDateStrip';
import { ScheduleDayGroup } from './components/ScheduleDayGroup';
import { EmptyState } from './components/EmptyState';
import { CancellationSheet, CancellationType } from './components/CancellationSheet';
import { BookingBottomSheet } from './components/BookingBottomSheet';
import RotatingLoader from '../ui/RotatingLoader';

const DAYS_AHEAD = 60;

export const PassSchedule = () => {
  const { t, i18n } = useTranslation();
  const language = useLanguage();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const [scheduleSearchParams] = useSearchParams();
  const showWelcome = scheduleSearchParams.get('welcome') === 'true';
  const { account, orders, passes, cancelOrder, cancelWaitlistItem } = useManagePasses();
  const { longDateFormat } = getDateFormat(account);
  const { scheduleData, isLoading } = usePassScheduleData();

  const dates = useMemo(() => {
    const result: string[] = [];
    for (let i = 0; i < DAYS_AHEAD; i++) {
      result.push(moment().add(i, 'days').format('YYYY-MM-DD'));
    }
    return result;
  }, []);

  const [selectedDate, setSelectedDate] = useState(dates[0]);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [sessionToCancel, setSessionToCancel] = useState<ScheduleSession | null>(null);
  const [sessionToBook, setSessionToBook] = useState<ScheduleSession | null>(null);
  const [cancelWaitlistDialogOpen, setCancelWaitlistDialogOpen] = useState(false);
  const [waitlistSessionToCancel, setWaitlistSessionToCancel] = useState<ScheduleSession | null>(null);
  const dayGroupRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const isScrollingFromTap = useRef(false);

  const sessionsByDate = useMemo(() => {
    const map = new Map<string, ScheduleSession[]>();
    for (const session of scheduleData) {
      const existing = map.get(session.date) || [];
      existing.push(session);
      map.set(session.date, existing);
    }
    return map;
  }, [scheduleData]);

  const datesWithSessions = useMemo(() => dates.filter((date) => sessionsByDate.has(date)), [dates, sessionsByDate]);

  const setDayGroupRef = useCallback((date: string, el: HTMLDivElement | null) => {
    if (el) {
      dayGroupRefs.current.set(date, el);
    } else {
      dayGroupRefs.current.delete(date);
    }
  }, []);

  useEffect(() => {
    if (datesWithSessions.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingFromTap.current) return;
        let topMostDate: string | null = null;
        let topMostTop = Infinity;
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.boundingClientRect.top < topMostTop) {
            topMostTop = entry.boundingClientRect.top;
            topMostDate = entry.target.getAttribute('data-date');
          }
        });
        if (topMostDate) {
          setSelectedDate(topMostDate);
        }
      },
      { rootMargin: '-120px 0px 0px 0px', threshold: 0 },
    );

    dayGroupRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [datesWithSessions]);

  const handleDateTap = (date: string) => {
    setSelectedDate(date);
    const el = dayGroupRefs.current.get(date);
    if (el) {
      isScrollingFromTap.current = true;
      const headerHeight = stickyHeaderRef.current?.offsetHeight || 0;
      const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
      window.scrollTo({ top, behavior: 'smooth' });
      setTimeout(() => {
        isScrollingFromTap.current = false;
      }, 800);
    }
  };

  const handleCancelClick = (session: ScheduleSession) => {
    setSessionToCancel(session);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async (confirmed: boolean) => {
    if (confirmed && sessionToCancel?.registeredOrder) {
      await cancelOrder(sessionToCancel.registeredOrder.id);
    }
    setCancelDialogOpen(false);
    setSessionToCancel(null);
  };

  const handleCancelWaitlistClick = (session: ScheduleSession) => {
    setWaitlistSessionToCancel(session);
    setCancelWaitlistDialogOpen(true);
  };

  const handleCancelWaitlistConfirm = async (confirmed: boolean) => {
    if (confirmed && waitlistSessionToCancel?.waitlistItemId) {
      await cancelWaitlistItem(waitlistSessionToCancel.waitlistItemId);
    }
    setCancelWaitlistDialogOpen(false);
    setWaitlistSessionToCancel(null);
  };

  const newestPassSelections = getNewestPass(passes)?.dated_session_selections ?? [];
  const failedSelections = newestPassSelections.filter(
    (selection) => selection.booking_status === DatedSessionBookingStatus.FAILED,
  );
  const failedSessionDateFormat = i18n.dir() === 'rtl' ? 'dddd D/M' : 'dddd MM/DD';

  if (isLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Grid container flexDirection="column" pb={4}>
      {showWelcome && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {t('manage_passes.schedule.welcome_message')}
        </Alert>
      )}
      {showWelcome && failedSelections.length > 0 && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
          <AlertTitle>
            {t('manage_passes.schedule.bookingFailedTitle', {
              booked: newestPassSelections.filter(
                (selection) => selection.booking_status === DatedSessionBookingStatus.BOOKED,
              ).length,
              total: newestPassSelections.length,
            })}
          </AlertTitle>
          {t('manage_passes.schedule.bookingFailedBody', {
            sessions: failedSelections
              .map(
                (selection) =>
                  `${moment(selection.date).format(failedSessionDateFormat)} ${moment
                    .utc(selection.start_time * 1000)
                    .format(getTimeFormat(account))}`,
              )
              .join(', '),
          })}
        </Alert>
      )}
      <Grid
        ref={stickyHeaderRef}
        container
        flexDirection="column"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'background.default',
          pb: 1,
        }}>
        <ScheduleHeader selectedDate={selectedDate} onTodayClick={() => handleDateTap(dates[0])} />
        <HorizontalDateStrip dates={dates} selectedDate={selectedDate} onSelectDate={handleDateTap} />
      </Grid>

      <Grid container flexDirection="column" gap={isSmallScreen ? 1.5 : 2}>
        {datesWithSessions.length > 0 ? (
          datesWithSessions.map((date) => (
            <div key={date} data-date={date} ref={(el) => setDayGroupRef(date, el)}>
              <ScheduleDayGroup
                date={date}
                sessions={sessionsByDate.get(date) || []}
                account={account}
                orders={orders}
                onCancel={handleCancelClick}
                onBook={setSessionToBook}
                onCancelWaitlist={handleCancelWaitlistClick}
              />
            </div>
          ))
        ) : (
          <EmptyState message={t('manage_passes.schedule.noSessions')} />
        )}
      </Grid>

      <BookingBottomSheet scheduleSession={sessionToBook} onClose={() => setSessionToBook(null)} />

      <CancellationSheet
        isOpen={cancelDialogOpen}
        onClose={handleCancelConfirm}
        experienceName={
          sessionToCancel && account ? getExperienceName(account, sessionToCancel.orderPageId, language) : ''
        }
        bookingDate={sessionToCancel?.date ? moment(sessionToCancel.date).format(longDateFormat) : ''}
        cancelHoursBefore={sessionToCancel ? getPassCancelHoursBefore(account, sessionToCancel.orderPageId) : undefined}
        cancelAllowed={
          !sessionToCancel ||
          isPassCancelAllowed(
            getPassCancelHoursBefore(account, sessionToCancel.orderPageId),
            sessionToCancel.date,
            sessionToCancel.timeSeconds,
          )
        }
      />

      <CancellationSheet
        isOpen={cancelWaitlistDialogOpen}
        onClose={handleCancelWaitlistConfirm}
        experienceName={
          waitlistSessionToCancel && account
            ? getExperienceName(account, waitlistSessionToCancel.orderPageId, language)
            : ''
        }
        bookingDate={waitlistSessionToCancel?.date ? moment(waitlistSessionToCancel.date).format(longDateFormat) : ''}
        cancellationType={CancellationType.WAITLIST}
      />
    </Grid>
  );
};
